import React from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import { ArtistPortalEarningsGroup } from 'fym-shared/src/smartlinks/artistPortals';
import { TypedMemo } from '../../../common/types';
import { useStreamingEarnings } from '../hooks/useStreamingEarnings';
import { sourceNames } from '../streamingSources';
import { streamingEarningsMessages as messages } from '../streamingEarningsMessages';
import { streamingBlockMessages } from '../streamingBlockMessages';
import { StreamingBlock } from './StreamingBlock';
import { EarningsMonthBars } from './EarningsMonthBars';
import { EarningsRowsTable } from './EarningsRowsTable';
import { withBrandWord } from '../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from '../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

const GROUP_TITLES: Record<ArtistPortalEarningsGroup, MessageDescriptor> = {
  [ArtistPortalEarningsGroup.period]: messages.perMonth,
  [ArtistPortalEarningsGroup.store]: messages.byStore,
  [ArtistPortalEarningsGroup.track]: messages.byTrack,
  [ArtistPortalEarningsGroup.country]: messages.byCountry,
};

const NAME_LABELS: Record<ArtistPortalEarningsGroup, MessageDescriptor> = {
  [ArtistPortalEarningsGroup.period]: streamingBlockMessages.colTrack,
  [ArtistPortalEarningsGroup.store]: streamingBlockMessages.colStore,
  [ArtistPortalEarningsGroup.track]: streamingBlockMessages.colTrack,
  [ArtistPortalEarningsGroup.country]: streamingBlockMessages.colCountry,
};

interface EarningsGroupBlockProps {
  accountId: number;
  groupBy: ArtistPortalEarningsGroup;
  months: { from: string; to: string };
  /**
   * True: draw nothing when the read has no row (By country: a source that gives no countries,
   * such as Ditto, shows no empty block). The loading and the error still show.
   */
  hideWhenEmpty?: boolean;
}

/**
 * "Net per month", "By store", "By track" or "By country" (design e1). Each currency has its own part; a part
 * of a second currency says why the amounts are apart.
 */
const EarningsGroupBlockComponent: React.FC<EarningsGroupBlockProps> = ({
  accountId,
  groupBy,
  months,
  hideWhenEmpty = false,
}) => {
  const intl = useIntl();
  const { groups, sources, isLoading, error, retry } = useStreamingEarnings({
    accountId,
    groupBy,
    months,
    skip: false,
  });
  const source = sourceNames(sources, 'shortName');
  const title = GROUP_TITLES[groupBy];
  const nameLabel = intl.formatMessage(NAME_LABELS[groupBy]);

  if (hideWhenEmpty && !isLoading && error === undefined && groups.length === 0) {
    return null;
  }

  return (
    <StreamingBlock
      title={intl.formatMessage(title)}
      isLoading={isLoading}
      error={error}
      retry={retry}
      testId={`streaming-earnings-${groupBy}`}
    >
      {groups.length === 0 ? (
        <p className={styles.faint}>{intl.formatMessage(streamingBlockMessages.emptyList)}</p>
      ) : null}
      {groups.map((group, index) => (
        <div
          key={group.currency ?? 'none'}
          className="d-flex flex-column gap-2"
          data-currency={group.currency ?? 'none'}
        >
          {index > 0 ? (
            <p className={styles.footnote}>
              {group.currency === null
                ? withBrandWord(intl.formatMessage(messages.otherNoCurrency))
                : withBrandWord(intl.formatMessage(messages.otherCurrency, { currency: group.currency, source }))}
            </p>
          ) : null}
          {groupBy === ArtistPortalEarningsGroup.period ? (
            <EarningsMonthBars rows={group.rows} currency={group.currency} />
          ) : (
            <EarningsRowsTable nameLabel={nameLabel} groupBy={groupBy} rows={group.rows} currency={group.currency} />
          )}
        </div>
      ))}
      {groupBy === ArtistPortalEarningsGroup.period ? (
        <p className={styles.footnote}>{intl.formatMessage(messages.perMonthNote)}</p>
      ) : null}
    </StreamingBlock>
  );
};

export const EarningsGroupBlock = TypedMemo(EarningsGroupBlockComponent);
